/**
 * PendingAssignTab Component
 * Shows checks approved by QC but not yet assigned to agents
 * With bulk select and filters (similar to PendingApprovalsTab)
 */
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle, UserCheck, X, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Check type display names
const CHECK_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Check',
  'court_advocate_check': 'Court Advocate Check',
  'criminal_advocate_check': 'Criminal Advocate Check',
  'id_check': 'ID Verification',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  'drug_test': 'Drug Test',
  'reference_check': 'Reference Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference',
  'global_database': 'Global Database Check',
  'world_criminal_db': 'World Criminal Database',
  'india_criminal_db': 'India Criminal Database'
};

const getCheckDisplayName = (checkType) => CHECK_NAMES[checkType] || checkType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A';

export default function PendingAssignTab({ 
  checkWisePending, 
  onRefresh,
  onShowCaseDetail 
}) {
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    checkType: 'all',
    client: 'all',
    sortBy: 'recent'
  });
  const [bulkAssigning, setBulkAssigning] = useState(false);
  
  // Individual assignment dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigningCheck, setAssigningCheck] = useState(null);
  const [assignType, setAssignType] = useState('agent'); // 'agent' or 'vendor'
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [agents, setAgents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assigning, setAssigning] = useState(false);
  
  // Fetch agents and vendors for assignment when dialog opens
  useEffect(() => {
    if (!showAssignDialog) return;
    
    const fetchAgentsAndVendors = async () => {
      try {
        const [agentsRes, vendorsRes] = await Promise.all([
          axios.get('/users?role=verification_team,inhouse_verification,vendor_management').catch(() => ({ data: [] })),
          axios.get('/subvendors').catch(() => ({ data: [] }))
        ]);
        
        // Filter to only active agents
        const relevantAgents = (agentsRes.data || []).filter(agent => {
          if (agent.is_active === false) return false;
          const role = agent.role || '';
          return role === 'inhouse_verification' || role === 'vendor_management' || role === 'verification_team';
        });
        
        setAgents(relevantAgents);
        setVendors(vendorsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch agents/vendors:', error);
      }
    };
    fetchAgentsAndVendors();
  }, [showAssignDialog]);

  const pendingAssignChecks = checkWisePending?.pending_assign_checks || [];
  
  // Get unique check types and clients for filters
  const uniqueCheckTypes = [...new Set(pendingAssignChecks.map(c => c.check_type))];
  const uniqueClients = [...new Set(pendingAssignChecks.map(c => c.client_name).filter(Boolean))];
  
  // Apply filters
  let filteredChecks = [...pendingAssignChecks];
  if (filters.checkType !== 'all') {
    filteredChecks = filteredChecks.filter(c => c.check_type === filters.checkType);
  }
  if (filters.client !== 'all') {
    filteredChecks = filteredChecks.filter(c => c.client_name === filters.client);
  }
  
  // Sort
  if (filters.sortBy === 'recent') {
    filteredChecks.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  } else if (filters.sortBy === 'oldest') {
    filteredChecks.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  }
  
  const allSelected = filteredChecks.length > 0 && selected.length === filteredChecks.length;
  
  const handleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(filteredChecks.map(c => ({ case_id: c.case_id, check_id: c.check_id })));
    }
  };
  
  const handleCheckSelect = (check) => {
    const key = `${check.case_id}-${check.check_id}`;
    const isSelected = selected.some(s => `${s.case_id}-${s.check_id}` === key);
    if (isSelected) {
      setSelected(selected.filter(s => `${s.case_id}-${s.check_id}` !== key));
    } else {
      setSelected([...selected, { case_id: check.case_id, check_id: check.check_id }]);
    }
  };
  
  const handleBulkAssign = async () => {
    if (selected.length === 0) {
      toast.error('Please select at least one check to assign');
      return;
    }
    
    setBulkAssigning(true);
    try {
      const response = await axios.post('/quick-approve/bulk-approve-assign', {
        check_ids: selected,
        auto_assign: true
      });
      
      toast.success(`Assigned ${response.data.assigned_count} check(s) to team`);
      setSelected([]);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign checks');
    } finally {
      setBulkAssigning(false);
    }
  };
  
  // Open assignment dialog for individual check
  const handleOpenAssignDialog = (check) => {
    setAssigningCheck(check);
    setAssignType('agent');
    setSelectedAgent('');
    setSelectedVendor('');
    setShowAssignDialog(true);
  };
  
  // Handle individual check assignment
  const handleAssignCheck = async () => {
    if (!assigningCheck) return;
    
    if (assignType === 'agent' && !selectedAgent) {
      toast.error('Please select an agent');
      return;
    }
    if (assignType === 'vendor' && !selectedVendor) {
      toast.error('Please select a vendor');
      return;
    }
    
    setAssigning(true);
    try {
      if (assignType === 'agent') {
        // Assign to internal agent
        await axios.post('/operations/assign-agent', {
          case_id: assigningCheck.case_id,
          check_id: assigningCheck.check_id,
          check_type: assigningCheck.check_type,
          agent_id: selectedAgent,
        });
      } else {
        // Assign to vendor
        await axios.post('/operations/assign-vendor', {
          case_id: assigningCheck.case_id,
          check_id: assigningCheck.check_id,
          check_type: assigningCheck.check_type,
          vendor_id: selectedVendor,
        });
      }
      
      toast.success(`Check assigned successfully to ${assignType === 'agent' ? 'agent' : 'vendor'}`);
      setShowAssignDialog(false);
      setAssigningCheck(null);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign check');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Pending Assignment</h1>
          <p className="text-slate-600">Checks approved by QC but not yet assigned to agents</p>
        </div>
        <Badge className="bg-cyan-100 text-cyan-700 text-lg px-4 py-2">
          {filteredChecks.length} of {pendingAssignChecks.length} Checks
        </Badge>
      </div>

      {/* Filters Row */}
      <Card className="border-slate-200 shadow-sm mb-4">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Check Type:</label>
              <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {uniqueCheckTypes.map(ct => (
                    <SelectItem key={ct} value={ct}>{ct?.replace(/_check/g, '').replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Client:</label>
              <Select value={filters.client} onValueChange={(v) => setFilters({...filters, client: v})}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {uniqueClients.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Sort:</label>
              <Select value={filters.sortBy} onValueChange={(v) => setFilters({...filters, sortBy: v})}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Recently Received" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Recently Approved</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setFilters({ checkType: 'all', client: 'all', sortBy: 'recent' })}
              className="text-slate-500"
            >
              <X className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selected.length > 0 && (
        <Card className="border-blue-300 bg-blue-50 shadow-sm mb-4">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">{selected.length} check(s) selected</span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setSelected([])}
                  className="border-slate-300"
                >
                  Clear Selection
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleBulkAssign}
                  disabled={bulkAssigning}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="bulk-assign-btn"
                >
                  {bulkAssigning ? 'Assigning...' : 'Auto-Assign to Team'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input 
                    type="checkbox" 
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-slate-300"
                    data-testid="select-all-assign-checkbox"
                  />
                </TableHead>
                <TableHead>Case ID</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Client / Entity</TableHead>
                <TableHead>Check Type</TableHead>
                <TableHead>Approved On</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredChecks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                    <p>No checks pending assignment</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredChecks.map((check, idx) => {
                  const isSelected = selected.some(s => `${s.case_id}-${s.check_id}` === `${check.case_id}-${check.check_id}`);
                  return (
                    <TableRow 
                      key={`${check.case_id}-${check.check_id}-${idx}`} 
                      className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <TableCell>
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={() => handleCheckSelect(check)}
                          className="w-4 h-4 rounded border-slate-300"
                          data-testid={`select-assign-check-${check.check_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium">{check.case_id || 'N/A'}</TableCell>
                      <TableCell>
                        <p className="font-medium">{check.employee_name || 'N/A'}</p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{check.client_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{check.entity_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {getCheckDisplayName(check.check_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {check.created_at ? new Date(check.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenAssignDialog(check)}
                          data-testid={`assign-check-${check.check_id}`}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Assign
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Individual Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Check</DialogTitle>
          </DialogHeader>
          
          {assigningCheck && (
            <div className="space-y-4 py-4">
              {/* Check Info */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Assigning check for:</p>
                <p className="font-medium">{assigningCheck.employee_name || 'N/A'}</p>
                <p className="text-sm text-slate-600">
                  {getCheckDisplayName(assigningCheck.check_type)} - {assigningCheck.case_id}
                </p>
              </div>
              
              {/* Assignment Type Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Assign To:</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={assignType === 'agent' ? 'default' : 'outline'}
                    onClick={() => setAssignType('agent')}
                    className="flex-1"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Internal Agent
                  </Button>
                  <Button
                    type="button"
                    variant={assignType === 'vendor' ? 'default' : 'outline'}
                    onClick={() => setAssignType('vendor')}
                    className="flex-1"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Vendor
                  </Button>
                </div>
              </div>
              
              {/* Agent/Vendor Selection */}
              {assignType === 'agent' ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Agent:</label>
                  <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name || agent.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Vendor:</label>
                  <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a vendor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignCheck} 
              disabled={assigning}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assigning ? 'Assigning...' : 'Assign Check'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
